import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/apiService';

const AutoBuyList = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [actionLoadingId, setActionLoadingId] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({
    dailyLimit: '',
    minAdult: '',
    minNight: '',
    ignorTicketBooked: false
  });

  const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPage));

  const fetchAutoBuyList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.post('autobuy/list', {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: search.trim(),
        status: statusFilter
      });

      setRows(response?.data || []);
      setTotalRecords(response?.recordsFiltered || 0);
    } catch (error) {
      console.error('Auto-buy list fetch error:', error);
      toast.error('Failed to fetch auto-buy settings');
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, search, statusFilter]);

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchAutoBuyList();
    }, search ? 350 : 0);

    return () => clearTimeout(delay);
  }, [fetchAutoBuyList, search]);

  const formatDate = (dateValue) => {
    if (!dateValue) return '--';
    return new Date(dateValue).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  };

  const formatCurrency = (value) => {
    const amount = Number(value || 0);
    return amount.toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    });
  };

const getVendorName = (item) => {
    const businessName = item?.userId?.business_name;
    if (businessName) return businessName;
    const firstName = item?.userId?.first_name || '';
    const lastName = item?.userId?.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || '--';
  };

  const getDestinationNames = (item) => {
    if (!Array.isArray(item?.destinations) || item.destinations.length === 0) return '--';
    return item.destinations
      .map((destination) => destination?.name)
      .filter(Boolean)
      .join(', ');
  };

  const getExcludeDepartureNames = (item) => {
    if (!Array.isArray(item?.excludeDeparture) || item.excludeDeparture.length === 0) return '--';
    return item.excludeDeparture
      .map((departure) => departure?.name)
      .filter(Boolean)
      .join(', ');
  };

  const handleToggleStatus = async (item) => {
    const nextStatus = !item.autoBuy;
    try {
      setActionLoadingId(item._id);
      const response = await apiService.put(`autobuy/status/${item._id}`, {
        autoBuy: nextStatus
      });
      if (response?.status) {
        setRows((prev) =>
          prev.map((row) =>
            row._id === item._id ? { ...row, autoBuy: nextStatus } : row
          )
        );
        toast.success(response.message || 'Status updated successfully');
      } else {
        toast.error(response?.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Auto-buy status toggle error:', error);
      toast.error('Failed to update status');
    } finally {
      setActionLoadingId('');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Are you sure you want to delete this auto-buy setting?');
    if (!confirmed) return;

    try {
      setActionLoadingId(id);
      const response = await apiService.delete(`autobuy/delete/${id}`);
      if (response?.status) {
        toast.success(response.message || 'Auto-buy setting deleted successfully');
        fetchAutoBuyList();
      } else {
        toast.error(response?.message || 'Failed to delete auto-buy setting');
      }
    } catch (error) {
      console.error('Auto-buy delete error:', error);
      toast.error('Failed to delete auto-buy setting');
    } finally {
      setActionLoadingId('');
    }
  };

  const handleEditOpen = (item) => {
    setEditingItem(item);
    setEditForm({
      dailyLimit: item?.dailyLimit ?? '',
      minAdult: item?.minAdult ?? '',
      minNight: item?.minNight ?? '',
      ignorTicketBooked: Boolean(item?.ignorTicketBooked)
    });
    setIsEditModalOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingItem?._id) return;

    const payload = {
      dailyLimit: editForm.dailyLimit === '' ? null : Number(editForm.dailyLimit),
      minAdult: editForm.minAdult === '' ? null : Number(editForm.minAdult),
      minNight: editForm.minNight === '' ? null : Number(editForm.minNight),
      ignorTicketBooked: Boolean(editForm.ignorTicketBooked)
    };

    try {
      setActionLoadingId(editingItem._id);
      const response = await apiService.put(`autobuy/update/${editingItem._id}`, payload);
      if (response?.status) {
        toast.success(response.message || 'Auto-buy setting updated successfully');
        setIsEditModalOpen(false);
        setEditingItem(null);
        fetchAutoBuyList();
      } else {
        toast.error(response?.message || 'Failed to update auto-buy setting');
      }
    } catch (error) {
      console.error('Auto-buy update error:', error);
      toast.error('Failed to update auto-buy setting');
    } finally {
      setActionLoadingId('');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-sky-50 to-cyan-50">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">AutoBuy Settings</h3>
              <p className="text-sm text-gray-600 mt-1">Manage all vendor auto-buy configurations</p>
            </div>
            <div className="px-3 py-2 rounded-lg bg-white border border-sky-100 text-sm text-gray-700">
              Total: <span className="font-semibold text-sky-700">{totalRecords}</span>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-5">
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="flex-1 min-w-[240px]">
              <div className="relative">
                <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search by vendor, email, phone, destination..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                />
              </div>
            </div>
            <div className="w-full sm:w-44">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-600">
                  <th className="px-4 py-3 text-left font-semibold">#</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[280px]">Vendor</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[320px]">Destinations</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[300px]">Exclude Departures</th>
                  <th className="px-4 py-3 text-left font-semibold">Daily Limit</th>
                  <th className="px-4 py-3 text-left font-semibold">Min Adult</th>
                  <th className="px-4 py-3 text-left font-semibold">Status</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[170px]">Updated At</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[180px]">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-10 text-center">
                      <div className="inline-flex items-center gap-2 text-gray-600">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#009ef7]"></div>
                        Loading auto-buy settings...
                      </div>
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-10 text-center text-gray-500">
                      No auto-buy settings found
                    </td>
                  </tr>
                ) : (
                  rows.map((item, index) => (
                    <tr key={item._id} className="border-t border-gray-100 hover:bg-sky-50/50">
                      <td className="px-4 py-3 font-medium text-gray-700">
                        {(currentPage - 1) * recordsPerPage + index + 1}
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/vendorWallet/${item?.userId?._id}`} className="font-semibold text-[#009ef7] no-underline hover:opacity-80">
                          {getVendorName(item)}
                        </Link>
                        <div className="text-xs text-sky-700 font-medium mt-0.5">
                          Wallet: {formatCurrency(item?.userId?.walletBalance)}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">{item?.userId?.email || '--'}</div>
                        <div className="text-xs text-gray-500">{item?.userId?.phone || '--'}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{getDestinationNames(item)}</td>
                      <td className="px-4 py-3 text-gray-700">{getExcludeDepartureNames(item)}</td>
                      <td className="px-4 py-3 text-gray-700">{item?.dailyLimit ?? '--'}</td>
                      <td className="px-4 py-3 text-gray-700">{item?.minAdult ?? '--'}</td>
                      <td className="px-4 py-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={Boolean(item?.autoBuy)}
                            onChange={() => handleToggleStatus(item)}
                            disabled={actionLoadingId === item._id}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-200 rounded-full peer peer-checked:bg-[#009ef7] peer-disabled:opacity-60 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                        </label>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{formatDate(item?.updatedAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditOpen(item)}
                            disabled={actionLoadingId === item._id}
                            className="px-2.5 py-1.5 text-xs font-semibold rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 disabled:opacity-60"
                          >
                            <i className="bi bi-pencil-square mr-1"></i>
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(item._id)}
                            disabled={actionLoadingId === item._id}
                            className="px-2.5 py-1.5 text-xs font-semibold rounded bg-rose-100 text-rose-700 hover:bg-rose-200 disabled:opacity-60"
                          >
                            <i className="bi bi-trash mr-1"></i>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!loading && totalRecords > 0 && (
            <div className="mt-4 bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs sm:text-sm text-gray-700">Show</span>
                <select
                  value={recordsPerPage}
                  onChange={(e) => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-xs sm:text-sm text-gray-700">entries</span>
              </div>
              <div className="flex items-center gap-1 flex-wrap justify-center">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100"
                >
                  «
                </button>
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className="px-3 py-1 border rounded text-xs sm:text-sm"
                        style={{
                          backgroundColor: currentPage === page ? '#009ef7' : '#fff',
                          borderColor: currentPage === page ? '#009ef7' : '#d1d5db',
                          color: currentPage === page ? '#fff' : '#374151'
                        }}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="px-2 text-gray-500">...</span>;
                  }
                  return null;
                })}
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100"
                >
                  »
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h4 className="text-lg font-semibold text-gray-900">Edit AutoBuy</h4>
              <button
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingItem(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Daily Limit</label>
                <input
                  type="number"
                  min="0"
                  value={editForm.dailyLimit}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, dailyLimit: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                  placeholder="Enter daily limit"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Adult</label>
                <input
                  type="number"
                  min="0"
                  value={editForm.minAdult}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, minAdult: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                  placeholder="Enter minimum adults"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Night</label>
                <input
                  type="number"
                  min="0"
                  value={editForm.minNight}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, minNight: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                  placeholder="Enter minimum nights"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="ignoreTicketBooked"
                  type="checkbox"
                  checked={editForm.ignorTicketBooked}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      ignorTicketBooked: e.target.checked
                    }))
                  }
                  className="h-4 w-4"
                />
                <label htmlFor="ignoreTicketBooked" className="text-sm text-gray-700">
                  Ignore Ticket Booked
                </label>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
              <button
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingItem(null);
                }}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={actionLoadingId === editingItem?._id}
                className="px-4 py-2 text-sm rounded-lg bg-[#009ef7] text-white hover:bg-[#0088d4] disabled:opacity-60"
              >
                {actionLoadingId === editingItem?._id ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoBuyList;


